/**
 * useDemoteElement — mutation for moving a LIVE element off the tracker.
 * Invalidates the same surfaces a requeue does, so the queue, the rotation
 * history and the element's timeline all reflect it immediately.
 * For Media Devoted | Created By Ciro Autiero
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { DemoteDestination, FlowLayer } from '@trafficpulse/shared';

import type { QueueProvider } from '@/features/trackers/flow-optimizer/queue-provider';
import { demoteElement } from '../../../services/flow-optimizer-queue-api';
import { queueKeys } from '../../../hooks/useFlowOptimizerQueue';

export interface DemoteInput {
  layer: FlowLayer;
  bankId: string;
  to: DemoteDestination;
  /** Authorise the floor swap the dialog named (see `useDemotePlan`). */
  coverFloor?: boolean;
}

export function useDemoteElement(
  campaignId: string,
  provider: QueueProvider = 'voluum',
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: DemoteInput) =>
      demoteElement(campaignId, input, provider),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: queueKeys.queue(provider, campaignId),
      });
      void queryClient.invalidateQueries({
        queryKey: queueKeys.rotationHistory(provider, campaignId),
      });
      // Prefix key — the element's timeline changed too.
      void queryClient.invalidateQueries({
        queryKey: [provider, 'flow-optimizer', 'queue-events', campaignId],
      });
    },
  });
}
